import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InlineSpinner } from "@/components/common/Loading";
import { Plus, GripVertical, ArrowUp, ArrowDown, Trash2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  text: "Single-line text", textarea: "Long text", number: "Number", currency: "Currency",
  date: "Date", datetime: "Date/time", dropdown: "Dropdown", multiselect: "Multi-select",
  checkbox: "Checkbox", yesno: "Yes/No", email: "Email", phone: "Phone", url: "URL",
};

export default function CustomFields() {
  const { currentAccount } = useAuth();
  const [fields, setFields] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [entity, setEntity] = useState("person");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/custom-fields", { params: { entity_type: entity } });
      setFields(data.items); setTypes(data.field_types); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [currentAccount, entity]);
  useEffect(() => { load(); }, [load]);

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return;
    const arr = [...fields];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setFields(arr);
    try { await api.post("/custom-fields/reorder", { ordered_ids: arr.map((f) => f.id) }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const del = async () => {
    try { await api.delete(`/custom-fields/${delTarget.id}`); toast.success("Field removed"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); setDelTarget(null); }
  };

  if (loading) return <PageLoader />;

  return (
    <div>
      <PageHeader title="Custom Fields" description="Add configurable fields to People or Agency / Business records. Drag order controls display order."
        actions={<Button className="bg-[#006090] hover:bg-[#004E75]" data-testid="add-custom-field" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" /> Add Field</Button>} />
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 mb-4" data-testid="cf-entity-switch">
        {[["person", "People"], ["business", "Agency / Business"]].map(([k, l]) => (
          <button key={k} onClick={() => setEntity(k)} data-testid={`cf-entity-${k}`}
            className={`px-3 py-1.5 text-sm rounded-md ${entity === k ? "bg-[#006090] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {fields.length === 0 ? (
        <EmptyState icon={SlidersHorizontal} title="No custom fields" description="Create fields to capture data specific to your recruiting process." />
      ) : (
        <div className="space-y-2" data-testid="custom-fields-list">
          {fields.map((f, i) => (
            <div key={f.id} data-testid={`field-${f.name}`} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <GripVertical className="w-4 h-4 text-slate-300" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{f.label}</span>
                  {f.required && <span className="text-xs text-rose-500">required</span>}
                  {f.hidden && <span className="text-xs text-slate-400">hidden</span>}
                  {f.read_only && <span className="text-xs text-slate-400">read-only</span>}
                </div>
                <p className="text-xs text-slate-400">
                  <span className="font-mono">{f.name}</span> · {TYPE_LABELS[f.field_type] || f.field_type} · {f.section}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`field-up-${f.name}`} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="w-4 h-4 text-slate-500" /></button>
                <button onClick={() => move(i, 1)} disabled={i === fields.length - 1} data-testid={`field-down-${f.name}`} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="w-4 h-4 text-slate-500" /></button>
                <button onClick={() => setDelTarget(f)} data-testid={`field-delete-${f.name}`} className="p-1 rounded hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <FieldDialog open={open} onOpenChange={setOpen} types={types} entity={entity} onSaved={() => { setOpen(false); load(); }} />
      <ConfirmDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)} title="Delete custom field?"
        description="Existing values on records are retained but the field will no longer be shown." destructive confirmLabel="Delete" onConfirm={del} />
    </div>
  );
}

function FieldDialog({ open, onOpenChange, types, entity, onSaved }) {
  const [form, setForm] = useState({ label: "", field_type: "text", help_text: "", section: "General",
    required: false, hidden: false, read_only: false, options: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const needsOptions = form.field_type === "dropdown" || form.field_type === "multiselect";

  const submit = async () => {
    if (!form.label) return setError("Label is required");
    setError(""); setSaving(true);
    try {
      const payload = { ...form, entity_type: entity,
        options: needsOptions ? form.options.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      await api.post("/custom-fields", payload);
      toast.success("Field created"); setForm({ label: "", field_type: "text", help_text: "", section: "General", required: false, hidden: false, read_only: false, options: "" });
      onSaved();
    } catch (e) { setError(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="custom-field-dialog">
        <DialogHeader><DialogTitle>Add Custom Field</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto tr-scroll pr-1">
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" data-testid="cf-label" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Type</Label>
              <Select value={form.field_type} onValueChange={(v) => setForm({ ...form, field_type: v })}>
                <SelectTrigger className="mt-1" data-testid="cf-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{types.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          {needsOptions && (
            <div><Label className="text-xs font-semibold text-slate-700">Options (comma separated)</Label>
              <Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} className="mt-1" data-testid="cf-options" placeholder="Option A, Option B" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Section</Label>
              <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Help text</Label>
              <Input value={form.help_text} onChange={(e) => setForm({ ...form, help_text: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="flex flex-wrap gap-4 pt-1">
            {[["required", "Required"], ["hidden", "Hidden"], ["read_only", "Read only"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`cf-${k}`} /> {l}
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cf-submit" className="bg-[#006090] hover:bg-[#004E75]">{saving ? <InlineSpinner /> : "Create Field"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
